import {
  alpha,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  AccountBalanceWallet,
  Add,
  Edit,
  ForwardToInbox,
  Payment,
  Receipt,
  TrendingUp,
} from "@mui/icons-material";
import {
  getFinanceOverview,
  sendBillById,
  getBillOverview,
  getWithdraws,
  getNurseryBill,
} from "apis";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  fetchBalance,
  fetchTransactions,
  FinanceState,
} from "store/features/finance";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { AuthState } from "store/features/auth";
import { useSnackbar } from "notistack";
import EditBillDialog from "components/EditBillDialog";
import InvoicePayoutDialog from "components/InvoicePayoutDialog";
import TablePaginationLocalizationProvider from "components/utils/TablePaginationLocalizationProvider";
import AddBillDialog from "components/AddBillDialog";
import AddWithdrawDialog from "components/AddWithdrawDialog";
import Flag from "react-world-flags";
import { formatAmount } from "utils";
import BillingFilterBar from "components/BillingFilterBar";

export interface ITransaction {
  heading: string;
  createdAt: string;
  ref: string;
  amount: any;
  currency: any;
  type: string;
  recieverId: string;
}

export interface IBillFromNursery {
  id: number;
  heading: string;
  ref: string;
  amount: number;
  type: string;
  status: number;
  childId: number | null;
  paidAt: string | null;
  createdAt: string;
  currencyId: number | null;
  currency?: {
    id: number;
    code: string;
    name: string;
    symbol: string;
    flag: string;
    rate: number;
    baseCurrency: string;
  };
}

export interface IBill {
  id: number;
  heading: string;
  createdAt: string;
  ref: string;
  amount: string;
  type: string;
  recieverId: string;
  status: number;
  nurseryId: number;
  billingStartDate?: string;
  billingEndDate?: string;
  currency?: {
    id: number;
    code: string;
    name: string;
    symbol: string;
    flag: string;
    rate: string;
    baseCurrency: string;
  };
}

export interface IInvoice {
  id: number;
  heading: string;
  createdAt: string;
  ref: string;
  amount: string;
  type: string;
  recieverId: string;
  childname: string;
  status: number;
  billingStartDate?: string;
  billingEndDate?: string;
  currency?:
    | string
    | {
        id: number;
        code: string;
        name: string;
        symbol: string;
        flag: string;
        rate: string;
        baseCurrency: string;
      };
}

export interface IWithdraw {
  id: number;
  createdAt: string;
  amount: string;
  currency: string;
  status: number;
  accountName: string;
  accountNumber: string;
  routingNumber: string;
}

type SortKey = "heading" | "amount" | "date" | "status";
type SortDir = "asc" | "desc";

const ADMIN_TABS = ["parent-bills", "withdrawals", "platform-bills"] as const;
type AdminTab = (typeof ADMIN_TABS)[number];

const Finance: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [withdrawPage, setWithdrawPage] = useState(0);
  const [withdrawRowsPerPage, setWithdrawRowsPerPage] = useState(10);
  const [platformPage, setPlatformPage] = useState(0);
  const [platformRowsPerPage, setPlatformRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"all" | "paid" | "unpaid">(
    "all",
  );
  const [withdrawFilter, setWithdrawFilter] = useState<
    "all" | "pending" | "completed" | "cancelled"
  >("all");
  const [platformFilter, setPlatformFilter] = useState<
    "all" | "paid" | "unpaid"
  >("all");
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const { balance, currency, transactions } = useSelector<
    RootState,
    FinanceState
  >((store) => store.finance);
  const { role } = useSelector<RootState, AuthState>((store) => store.auth);

  const [invoices, setInvoices] = useState<IBill[]>([]);
  const [billings, setBillings] = useState<IInvoice[]>([]);
  const [billsFromNursery, setBillsFromNursery] = useState<any[]>([]);
  const [withdraws, setWithdraws] = useState<IWithdraw[]>([]);

  const [openEditBillDialog, setOpenEditBillDialog] = useState<any>();
  const [openAddBillDialog, setOpenAddBillDialog] = useState(false);
  const [invoiceId, setInvoiceId] = useState<number>();
  const [billsFromNurseryId, setBillsFromNurseryId] = useState<number>();
  const [open, setOpen] = useState(false);
  const [openWithdraw, setOpenWithdraw] = useState(false);

  // Payment callback: Chargily redirects the user to back_url after
  // checkout. Server-side, the bill status is updated via Chargily's
  // webhook (separate from this client-side return). The user just
  // sees a toast and lands back on /finance.
  useEffect(() => {
    const success = searchParams.get("success");
    if (success === "true") {
      enqueueSnackbar(t("finance.payment_success"), { variant: "success" });
      navigate("/finance", { replace: true });
    } else if (success === "false") {
      enqueueSnackbar(t("finance.payment_error"), { variant: "error" });
      navigate("/finance", { replace: true });
    }
  }, [searchParams, navigate, enqueueSnackbar, t]);

  const fetchAdminData = useCallback(() => {
    setLoading(true);
    Promise.all([
      getBillOverview().catch(() => ({
        data: { balance: 0, currency: "", recentTransactions: [], bills: [] },
      })),
      getWithdraws().catch(() => []),
      getNurseryBill().catch(() => [[]]),
    ])
      .then(([overview, wdraws, nurseryBills]) => {
        const {
          balance: bal,
          currency: cur,
          recentTransactions,
          bills,
        } = overview.data;
        dispatch(fetchBalance({ balance: bal, currency: cur }));
        dispatch(fetchTransactions(recentTransactions));
        setInvoices(bills || []);
        setWithdraws(wdraws || []);
        setBillsFromNursery((nurseryBills as any)?.[0]?.data || []);
      })
      .finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (role === "NURSERY_ADMIN") {
      fetchAdminData();
    } else if (role === "PARENT") {
      setLoading(true);
      getFinanceOverview()
        .then(
          ({
            data: {
              balance: bal,
              currency: cur,
              recentTransactions,
              transactions: txns,
            },
          }) => {
            setBillings(txns || []);
            dispatch(fetchBalance({ balance: bal, currency: cur }));
            dispatch(fetchTransactions(recentTransactions));
          },
        )
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [dispatch, openWithdraw, role, fetchAdminData]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  // For NURSERY_ADMIN, the top "owed to you" view is bills the nursery
  // sent to parents (parent_bill). Subscription bills the nursery owes the
  // platform (nursery_bill, in `invoices`) get their own dedicated card
  // below — different direction, different actions, different audience.
  const currentBills: (IInvoice | IBill)[] =
    role === "PARENT" ? billings : (billsFromNursery as IBill[]);

  const filteredBills = useMemo(() => {
    let result = currentBills;
    if (statusFilter === "paid") result = result.filter((b) => b.status === 1);
    else if (statusFilter === "unpaid")
      result = result.filter((b) => b.status === 0);
    return result;
  }, [currentBills, statusFilter]);

  const sortedBills = useMemo(() => {
    const sorted = filteredBills.slice();
    const dir = sortDir === "asc" ? 1 : -1;
    sorted.sort((a, b) => {
      switch (sortKey) {
        case "heading":
          return dir * (a.heading || "").localeCompare(b.heading || "");
        case "amount":
          return dir * (parseFloat(a.amount) - parseFloat(b.amount));
        case "date":
          return (
            dir *
            (new Date(a.billingStartDate || a.createdAt || 0).getTime() -
              new Date(b.billingStartDate || b.createdAt || 0).getTime())
          );
        case "status":
          return dir * (a.status - b.status);
        default:
          return 0;
      }
    });
    return sorted;
  }, [filteredBills, sortKey, sortDir]);

  const paidCount = currentBills.filter((b) => b.status === 1).length;
  const unpaidCount = currentBills.filter((b) => b.status === 0).length;

  // Headline: PARENT keeps using the server-provided balance. NURSERY_ADMIN
  // computes locally from currentBills (parent_bill rows the nursery sent)
  // because the server `balance` aggregates a different table and currently
  // also miscounts paid bills as outstanding (the /finance "owed to you"
  // bug seen on UAT).
  const headlineAmount =
    role === "NURSERY_ADMIN"
      ? currentBills
          .filter((b) => b.status === 0)
          .reduce((s, b: any) => s + Number(b.amount || 0), 0)
      : balance;
  const headlineCurrency =
    role === "NURSERY_ADMIN"
      ? (currentBills[0] as any)?.currency?.code || currency || ""
      : currency || "";

  // Tabs apply to NURSERY_ADMIN only — parents see their bills directly
  // without the tab UI. Plain in-memory state: refresh resets to the
  // default tab. Deliberate — this is an internal admin tool where
  // bookmarking individual tabs isn't a workflow worth the URL clutter.
  const [activeTab, setActiveTab] = useState<AdminTab>("parent-bills");
  const handleTabChange = (_: unknown, value: AdminTab) => setActiveTab(value);
  const showParentBills =
    role === "PARENT" ||
    (role === "NURSERY_ADMIN" && activeTab === "parent-bills");
  const showWithdrawalsTab =
    role === "NURSERY_ADMIN" && activeTab === "withdrawals";
  const showPlatformBillsTab =
    role === "NURSERY_ADMIN" && activeTab === "platform-bills";

  // Withdrawals tab stats. Status convention: 0=pending, 1=completed,
  // 2=cancelled. Anything outside that set is treated as pending so
  // unfamiliar codes don't silently disappear from the headline.
  const withdrawCompletedCount = withdraws.filter((w) => w.status === 1).length;
  const withdrawCancelledCount = withdraws.filter((w) => w.status === 2).length;
  const withdrawPendingCount =
    withdraws.length - withdrawCompletedCount - withdrawCancelledCount;
  const withdrawPendingAmount = withdraws
    .filter((w) => w.status !== 1 && w.status !== 2)
    .reduce((s, w) => s + Number(w.amount || 0), 0);
  const withdrawCurrency = (withdraws[0] as any)?.currency || "";
  const filteredWithdraws = withdraws.filter((w) => {
    if (withdrawFilter === "all") return true;
    if (withdrawFilter === "completed") return w.status === 1;
    if (withdrawFilter === "cancelled") return w.status === 2;
    return w.status !== 1 && w.status !== 2; // pending
  });

  // Platform bills tab stats. Status convention: 0=unpaid, 1=paid.
  const platformPaidCount = invoices.filter((b) => b.status === 1).length;
  const platformUnpaidCount = invoices.filter((b) => b.status === 0).length;
  const platformOwedAmount = invoices
    .filter((b) => b.status === 0)
    .reduce((s, b: any) => s + Number(b.amount || 0), 0);
  const platformCurrency = (invoices[0] as any)?.currency?.code || "";
  const filteredInvoices = invoices.filter((b) => {
    if (platformFilter === "all") return true;
    if (platformFilter === "paid") return b.status === 1;
    return b.status === 0;
  });

  const handleSendClick = async (bill: any) => {
    try {
      await sendBillById(bill.id);
      enqueueSnackbar(t("finance.invoice_sent"), { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || t("finance.send_error"), {
        variant: "error",
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {role === "NURSERY_ADMIN" && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          sx={{
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
            px: 2,
          }}
        >
          <Tab
            value="parent-bills"
            label={t("finance.tabs.parent_bills")}
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
          <Tab
            value="withdrawals"
            label={t("finance.tabs.withdrawal_requests")}
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
          <Tab
            value="platform-bills"
            label={t("finance.tabs.platform_bills")}
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
        </Tabs>
      )}

      <Container sx={{ py: 2 }}>
        {showParentBills && (
          <>
            {/* Tab header — outstanding amount + counts + Add Bill */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mb: 2,
              }}
            >
              <AccountBalanceWallet
                sx={{ color: "primary.main", fontSize: 24 }}
              />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  {role === "NURSERY_ADMIN"
                    ? t("finance.outstanding")
                    : t("finance.balance")}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={
                    headlineAmount <= 0
                      ? "success.main"
                      : role === "NURSERY_ADMIN"
                        ? "warning.main"
                        : "error.main"
                  }
                >
                  {role === "NURSERY_ADMIN"
                    ? `${Math.abs(headlineAmount).toLocaleString()} ${headlineCurrency} ${t("finance.owed_to_you").toLowerCase()}`
                    : `${headlineAmount <= 0 ? t("finance.good") : t("finance.bad")} ${Math.abs(headlineAmount).toLocaleString()} ${headlineCurrency}`}
                </Typography>
              </Box>

              <Box
                sx={{
                  ml: { xs: 0, sm: "auto" },
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Chip
                  label={`${paidCount} ${role === "NURSERY_ADMIN" ? t("finance.received") : t("labels.paid")}`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={`${unpaidCount} ${role === "NURSERY_ADMIN" ? t("finance.owed_to_you") : t("labels.unpaid")}`}
                  size="small"
                  color={role === "NURSERY_ADMIN" ? "warning" : "error"}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
                {role === "NURSERY_ADMIN" && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setOpenAddBillDialog(true)}
                    sx={{ ml: 1, textTransform: "none", fontWeight: 600 }}
                  >
                    {t("bill.add")}
                  </Button>
                )}
              </Box>
            </Box>

            {/* Status filter */}
            <Box display="flex" gap={1} mb={2} alignItems="center">
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontSize: 10,
                  mr: 0.5,
                }}
              >
                {t("labels.status")}
              </Typography>
              {(["all", "paid", "unpaid"] as const).map((val) => (
                <Chip
                  key={val}
                  label={val === "all" ? t("posts.all") : t(`labels.${val}`)}
                  size="small"
                  variant={statusFilter === val ? "filled" : "outlined"}
                  color={
                    val === "paid"
                      ? "success"
                      : val === "unpaid"
                        ? "error"
                        : statusFilter === val
                          ? "primary"
                          : "default"
                  }
                  onClick={() => {
                    setStatusFilter(val);
                    setPage(0);
                  }}
                  sx={{
                    fontWeight: statusFilter === val ? 600 : 400,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                />
              ))}
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ ml: "auto" }}
              >
                {sortedBills.length}{" "}
                {sortedBills.length === 1
                  ? t("finance.record")
                  : t("finance.records")}
              </Typography>
            </Box>

            {/* Invoices table */}
            <Card
              variant="outlined"
              sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "heading"}
                          direction={sortKey === "heading" ? sortDir : "asc"}
                          onClick={() => handleSort("heading")}
                        >
                          {t("labels.heading")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("labels.ref")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("labels.currency")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "amount"}
                          direction={sortKey === "amount" ? sortDir : "asc"}
                          onClick={() => handleSort("amount")}
                        >
                          {t("labels.amount")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "date"}
                          direction={sortKey === "date" ? sortDir : "desc"}
                          onClick={() => handleSort("date")}
                        >
                          {t("labels.date")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "status"}
                          direction={sortKey === "status" ? sortDir : "asc"}
                          onClick={() => handleSort("status")}
                        >
                          {t("labels.status")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">
                        {t("labels.action")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedBills.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <Typography color="text.secondary">
                            {statusFilter === "paid"
                              ? t("finance.no_paid_bills")
                              : statusFilter === "unpaid"
                                ? t("finance.no_unpaid_bills")
                                : t("finance.no_bill")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedBills
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .map((bill) => (
                          <TableRow key={bill.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {bill.heading || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {bill.ref || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={0.75}
                              >
                                {typeof bill.currency !== "string" &&
                                  bill.currency?.flag && (
                                    <Flag
                                      code={bill.currency.flag}
                                      className="w-5 h-3.5"
                                    />
                                  )}
                                <Typography variant="body2">
                                  {typeof bill.currency === "string"
                                    ? bill.currency
                                    : bill.currency?.code || "—"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {typeof bill.currency !== "string"
                                  ? bill.currency?.symbol
                                  : ""}
                                {formatAmount(bill.amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {bill.billingStartDate
                                  ? dayjs(bill.billingStartDate).format(
                                      "DD MMM",
                                    )
                                  : "—"}
                                {bill.billingEndDate
                                  ? ` — ${dayjs(bill.billingEndDate).format("DD MMM YYYY")}`
                                  : ""}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  bill.status === 1
                                    ? t("labels.paid")
                                    : t("labels.unpaid")
                                }
                                color={bill.status === 1 ? "success" : "error"}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {role === "NURSERY_ADMIN" && (
                                <Box
                                  display="inline-flex"
                                  gap={0.5}
                                  alignItems="center"
                                >
                                  <Tooltip title={t("bill.edit")}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setOpenEditBillDialog(bill)
                                      }
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t("bill.send")}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleSendClick(bill)}
                                    >
                                      <ForwardToInbox fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                              {bill.status === 0 && role === "PARENT" && (
                                <Tooltip title={t("buttons.pay")}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Payment />}
                                    onClick={(e) => {
                                      setInvoiceId(bill.id);
                                      setOpen(true);
                                    }}
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: "none",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {t("buttons.pay")}
                                  </Button>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider />
              <TablePaginationLocalizationProvider>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={sortedBills.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </TablePaginationLocalizationProvider>
            </Card>
          </>
        )}

        {/* Withdrawals tab */}
        {showWithdrawalsTab && (
          <>
            {/* Tab header — pending amount + counts + New Withdraw */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mb: 2,
              }}
            >
              <AccountBalanceWallet
                sx={{ color: "primary.main", fontSize: 24 }}
              />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  {t("withdraw.label")}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={
                    withdrawPendingAmount === 0
                      ? "success.main"
                      : "warning.main"
                  }
                >
                  {`${withdrawPendingAmount.toLocaleString()} ${withdrawCurrency} ${t("labels.pending").toLowerCase()}`}
                </Typography>
              </Box>

              <Box
                sx={{
                  ml: { xs: 0, sm: "auto" },
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Chip
                  label={`${withdrawCompletedCount} ${t("labels.completed")}`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={`${withdrawPendingCount} ${t("labels.pending")}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
                {withdrawCancelledCount > 0 && (
                  <Chip
                    label={`${withdrawCancelledCount} ${t("labels.cancelled")}`}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setOpenWithdraw(true)}
                  sx={{ ml: 1, textTransform: "none", fontWeight: 600 }}
                >
                  {t("withdraw.add")}
                </Button>
              </Box>
            </Box>

            {/* Status filter */}
            <Box display="flex" gap={1} mb={2} alignItems="center">
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontSize: 10,
                  mr: 0.5,
                }}
              >
                {t("labels.status")}
              </Typography>
              {(["all", "pending", "completed", "cancelled"] as const).map(
                (val) => (
                  <Chip
                    key={val}
                    label={val === "all" ? t("posts.all") : t(`labels.${val}`)}
                    size="small"
                    variant={withdrawFilter === val ? "filled" : "outlined"}
                    color={
                      val === "completed"
                        ? "success"
                        : val === "cancelled"
                          ? "error"
                          : val === "pending"
                            ? "warning"
                            : withdrawFilter === val
                              ? "primary"
                              : "default"
                    }
                    onClick={() => {
                      setWithdrawFilter(val);
                      setWithdrawPage(0);
                    }}
                    sx={{
                      fontWeight: withdrawFilter === val ? 600 : 400,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  />
                ),
              )}
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ ml: "auto" }}
              >
                {filteredWithdraws.length}{" "}
                {filteredWithdraws.length === 1
                  ? t("finance.record")
                  : t("finance.records")}
              </Typography>
            </Box>

            <Card variant="outlined" sx={{ borderRadius: 3, p: 2.5, mb: 2 }}>
              {filteredWithdraws.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: "center" }}
                >
                  {withdrawFilter === "pending"
                    ? t("finance.no_pending_withdrawals")
                    : withdrawFilter === "completed"
                      ? t("finance.no_completed_withdrawals")
                      : withdrawFilter === "cancelled"
                        ? t("finance.no_cancelled_withdrawals")
                        : t("finance.no_withdraws")}
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("labels.date")}</TableCell>
                          <TableCell>{t("labels.amount")}</TableCell>
                          <TableCell>{t("labels.account")}</TableCell>
                          <TableCell align="center">
                            {t("labels.status")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredWithdraws
                          .slice(
                            withdrawPage * withdrawRowsPerPage,
                            withdrawPage * withdrawRowsPerPage +
                              withdrawRowsPerPage,
                          )
                          .map((w) => (
                            <TableRow key={w.id} hover>
                              <TableCell>
                                {dayjs(w.createdAt).format("DD MMM YYYY")}
                              </TableCell>
                              <TableCell>
                                {formatAmount(Number(w.amount))} {w.currency}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {w.accountName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {w.accountNumber}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={
                                    w.status === 1
                                      ? t("labels.completed")
                                      : w.status === 2
                                        ? t("labels.cancelled")
                                        : t("labels.pending")
                                  }
                                  color={
                                    w.status === 1
                                      ? "success"
                                      : w.status === 2
                                        ? "error"
                                        : "warning"
                                  }
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePaginationLocalizationProvider>
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50]}
                      component="div"
                      count={filteredWithdraws.length}
                      rowsPerPage={withdrawRowsPerPage}
                      page={withdrawPage}
                      onPageChange={(_, p) => setWithdrawPage(p)}
                      onRowsPerPageChange={(e) => {
                        setWithdrawRowsPerPage(parseInt(e.target.value, 10));
                        setWithdrawPage(0);
                      }}
                    />
                  </TablePaginationLocalizationProvider>
                </>
              )}
            </Card>
          </>
        )}

        {/* Platform Bills tab — what the nursery owes the platform */}
        {showPlatformBillsTab && (
          <>
            {/* Tab header — owed amount + counts */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mb: 2,
              }}
            >
              <Receipt sx={{ color: "primary.main", fontSize: 24 }} />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  {t("finance.platform_bills")}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={
                    platformOwedAmount === 0 ? "success.main" : "warning.main"
                  }
                >
                  {`${platformOwedAmount.toLocaleString()} ${platformCurrency} ${t("finance.owed_to_platform")}`}
                </Typography>
              </Box>

              <Box
                sx={{
                  ml: { xs: 0, sm: "auto" },
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Chip
                  label={`${platformPaidCount} ${t("labels.paid")}`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={`${platformUnpaidCount} ${t("labels.unpaid")}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>

            {/* Status filter */}
            <Box display="flex" gap={1} mb={2} alignItems="center">
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontSize: 10,
                  mr: 0.5,
                }}
              >
                {t("labels.status")}
              </Typography>
              {(["all", "paid", "unpaid"] as const).map((val) => (
                <Chip
                  key={val}
                  label={val === "all" ? t("posts.all") : t(`labels.${val}`)}
                  size="small"
                  variant={platformFilter === val ? "filled" : "outlined"}
                  color={
                    val === "paid"
                      ? "success"
                      : val === "unpaid"
                        ? "warning"
                        : platformFilter === val
                          ? "primary"
                          : "default"
                  }
                  onClick={() => {
                    setPlatformFilter(val);
                    setPlatformPage(0);
                  }}
                  sx={{
                    fontWeight: platformFilter === val ? 600 : 400,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                />
              ))}
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ ml: "auto" }}
              >
                {filteredInvoices.length}{" "}
                {filteredInvoices.length === 1
                  ? t("finance.record")
                  : t("finance.records")}
              </Typography>
            </Box>

            <Card variant="outlined" sx={{ borderRadius: 3, p: 2.5, mb: 2 }}>
              {filteredInvoices.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: "center" }}
                >
                  {platformFilter === "paid"
                    ? t("finance.no_paid_platform_bills")
                    : platformFilter === "unpaid"
                      ? t("finance.no_unpaid_platform_bills")
                      : t("finance.no_platform_bills")}
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("labels.heading")}</TableCell>
                          <TableCell>{t("labels.ref")}</TableCell>
                          <TableCell>{t("labels.amount")}</TableCell>
                          <TableCell>{t("labels.date")}</TableCell>
                          <TableCell align="center">
                            {t("labels.status")}
                          </TableCell>
                          <TableCell align="center">
                            {t("labels.action")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredInvoices
                          .slice(
                            platformPage * platformRowsPerPage,
                            platformPage * platformRowsPerPage +
                              platformRowsPerPage,
                          )
                          .map((b) => (
                            <TableRow key={b.id} hover>
                              <TableCell>{b.heading}</TableCell>
                              <TableCell>{b.ref}</TableCell>
                              <TableCell>
                                {formatAmount(Number(b.amount))}{" "}
                                {(b as any).currency?.code || ""}
                              </TableCell>
                              <TableCell>
                                {b.createdAt
                                  ? dayjs(b.createdAt).format("DD MMM YYYY")
                                  : "-"}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={
                                    b.status === 1
                                      ? t("labels.paid")
                                      : t("labels.unpaid")
                                  }
                                  color={b.status === 1 ? "success" : "warning"}
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                {b.status === 0 && (
                                  <Tooltip title={t("buttons.pay")}>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<Payment />}
                                      onClick={() => {
                                        setInvoiceId(b.id);
                                        setOpen(true);
                                      }}
                                      sx={{
                                        borderRadius: 2,
                                        textTransform: "none",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {t("buttons.pay")}
                                    </Button>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePaginationLocalizationProvider>
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50]}
                      component="div"
                      count={filteredInvoices.length}
                      rowsPerPage={platformRowsPerPage}
                      page={platformPage}
                      onPageChange={(_, p) => setPlatformPage(p)}
                      onRowsPerPageChange={(e) => {
                        setPlatformRowsPerPage(parseInt(e.target.value, 10));
                        setPlatformPage(0);
                      }}
                    />
                  </TablePaginationLocalizationProvider>
                </>
              )}
            </Card>
          </>
        )}

        {/* Recent transactions — PARENT view only; admin tabs replace it */}
        {role === "PARENT" && (
          <Card variant="outlined" sx={{ borderRadius: 3, p: 2.5 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1.5}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUp sx={{ color: "primary.main", fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  {t("finance.recent")}
                </Typography>
              </Box>
              <Button
                variant="text"
                size="small"
                component={Link}
                to="/finance/transactions"
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {t("view_all")}
              </Button>
            </Box>
            {transactions.items.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 2, textAlign: "center" }}
              >
                {t("finance.no_bill")}
              </Typography>
            ) : (
              <List disablePadding>
                {transactions.items.slice(0, 5).map((item) => (
                  <ListItem key={item.ref} sx={{ px: 0, py: 0.75 }}>
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      {item.type === "bill" ? (
                        <ReceiptIcon color="error" fontSize="small" />
                      ) : (
                        <LocalAtmIcon color="success" fontSize="small" />
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {item.heading}
                        </Typography>
                      }
                      secondary={dayjs(item.createdAt).format("DD MMM YYYY")}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Card>
        )}
      </Container>

      {/* Dialogs */}
      {open && (
        <InvoicePayoutDialog
          open={open}
          onClose={() => setOpen(false)}
          id={invoiceId as number}
        />
      )}
      {openWithdraw && (
        <AddWithdrawDialog
          open={openWithdraw}
          onClose={() => setOpenWithdraw(false)}
        />
      )}
      {openEditBillDialog && (
        <EditBillDialog
          open={openEditBillDialog}
          onClose={() => setOpenEditBillDialog(null)}
          onBillUpdate={fetchAdminData}
        />
      )}
      {openAddBillDialog && (
        <AddBillDialog
          open={openAddBillDialog}
          onClose={() => setOpenAddBillDialog(false)}
          onBillAdd={fetchAdminData}
        />
      )}
    </>
  );
};

export default Finance;
